// Common view state (zustand): language · theme · view scope · period · usage method · team · person.
// The mockup App.state + App.set rules, minus the mockup-only parts (role switching, demo, tour, fake user).
// Permission-dependent fixes (allowed scopes, pickable teams) happen in useView(), which knows the effective permissions;
// the store keeps what the person asked for (scopeWanted, team) so a screen that allows more opens with it again.
import { create } from 'zustand';
import { isCustomPeriod, type Period } from '../lib/time';
import { store as storage } from '../lib/util';
import { readUrlState, validLang, validTheme, type Lang, type Method, type Theme, type UrlNote, type ViewScope } from './url';

export type { Lang, Method, Theme, ViewScope, UrlNote };

export interface ViewValues {
  lang: Lang;
  theme: Theme;
  /** The scope the person picked (or the link carried); the effective scope is decided per screen by useView(). */
  scopeWanted: ViewScope | null;
  period: Period;
  method: Method;
  /** Team filter inside "all" (team id) — dropped by useView() when the screen or the person cannot use it. */
  team: string | null;
  /** One person inside the view scope (person id). */
  person: string | null;
}

export interface ViewState extends ViewValues {
  /** Notices about address values that were changed on opening (shown once above the first screen). */
  urlNotes: UrlNote[];
  setLang(lang: Lang): void;
  setTheme(theme: Theme): void;
  setScope(scope: ViewScope): void;
  setPeriod(period: Period): void;
  setMethod(method: Method): void;
  setTeam(team: string | null): void;
  setPerson(person: string | null): void;
  /** Several values at once (URL reads, links that carry conditions). */
  apply(patch: Partial<ViewValues>): void;
  addUrlNotes(notes: UrlNote[]): void;
  clearUrlNotes(): void;
}

const samePeriod = (a: Period, b: Period) =>
  isCustomPeriod(a) && isCustomPeriod(b) ? a.from === b.from && a.to === b.to : a === b;

/** First values: address query first (shared links), then this browser's saved language/theme, then defaults. */
export function initialViewValues(search: string = typeof location !== 'undefined' ? location.search : '', now: number = Date.now()): ViewValues & { urlNotes: UrlNote[] } {
  const u = readUrlState(search, now);
  const notes: UrlNote[] = [];
  if (u.invalid.includes('period')) notes.push({ key: 'url.fix.period', params: { gotKey: 'period.long.7d' } });
  if (u.invalid.includes('method')) notes.push({ key: 'url.fix.method' });
  if (u.invalid.includes('team')) notes.push({ key: 'url.fix.team' });
  return {
    lang: u.lang ?? validLang(storage.get('lang') ?? undefined) ?? 'ko',
    theme: u.theme ?? validTheme(storage.get('theme') ?? undefined) ?? 'system',
    scopeWanted: u.scope ?? null,
    period: u.period ?? '7d',
    method: u.method ?? 'all',
    team: u.team ?? null,
    person: u.person ?? null,
    urlNotes: notes,
  };
}

export const useViewStore = create<ViewState>()((set, get) => ({
  ...initialViewValues(),
  setLang(lang) {
    if (get().lang === lang) return;
    storage.set('lang', lang);
    set({ lang });
  },
  setTheme(theme) {
    if (get().theme === theme) return;
    storage.set('theme', theme);
    set({ theme });
  },
  setScope(scope) {
    // leaving "all" drops the team filter; "me" drops the one-person filter (me is the person)
    set((s) => ({ scopeWanted: scope, team: scope === 'all' ? s.team : null, person: scope === 'me' ? null : s.person }));
  },
  setPeriod(period) {
    if (!samePeriod(get().period, period)) set({ period });
  },
  setMethod(method) {
    if (get().method !== method) set({ method });
  },
  setTeam(team) {
    if (get().team !== team) set({ team });
  },
  setPerson(person) {
    if (get().person !== person) set({ person });
  },
  apply(patch) {
    const s = get();
    const next: Partial<ViewValues> = {};
    if (patch.lang && patch.lang !== s.lang) {
      storage.set('lang', patch.lang);
      next.lang = patch.lang;
    }
    if (patch.theme && patch.theme !== s.theme) {
      storage.set('theme', patch.theme);
      next.theme = patch.theme;
    }
    if (patch.scopeWanted !== undefined && patch.scopeWanted !== s.scopeWanted) next.scopeWanted = patch.scopeWanted;
    if (patch.period && !samePeriod(patch.period, s.period)) next.period = patch.period;
    if (patch.method && patch.method !== s.method) next.method = patch.method;
    if (patch.team !== undefined && patch.team !== s.team) next.team = patch.team;
    if (patch.person !== undefined && patch.person !== s.person) next.person = patch.person;
    if (Object.keys(next).length) set(next);
  },
  addUrlNotes(notes) {
    if (notes.length) set((s) => ({ urlNotes: [...s.urlNotes, ...notes.filter((n) => !s.urlNotes.some((x) => x.key === n.key))] }));
  },
  clearUrlNotes() {
    if (get().urlNotes.length) set({ urlNotes: [] });
  },
}));

/** Reset to defaults (tests, sign-out). */
export function resetViewStore(search = ''): void {
  useViewStore.setState({ ...initialViewValues(search) });
}
